import { FaxInput } from '@/components/form/fax-input';
import { PhoneInput } from '@/components/form/phone-input';
import States from '@/components/states';
import { Divider } from '@/components/ui/Divider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Flex, Input, Spinner } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, FormProvider, Resolver, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Page } from '../../../components/layout/page';
import { CreateAddressDto, CreatePharmacyDto, PharmacyDto, UpdatePharmacyDto } from '../../../lib/ServerApi';
import { api } from '../../../lib/api';
import { queryClient } from '../../../providers/query-client';
import { addressOptionalValidationSchema } from '../../../validations/address';

const validationSchema: Resolver<CreatePharmacyDto> = zodResolver(
    z.object({
        name: z.string().min(1, 'name is required'),
        phone: z.string().min(10, 'phone is required'),
        fax: z.string().nullish(),
        address: addressOptionalValidationSchema,
    }),
);

export function PharmacyForm() {
    const navigate = useNavigate();
    const params = useParams<'pharmacyId'>();

    const rhf = useForm<CreatePharmacyDto>({
        defaultValues: {
            name: '',
            phone: '',
            fax: null,
            address: {
                address: '',
                city: '',
                state: '',
                zipcode: '',
            },
        },
        resolver: validationSchema,
        reValidateMode: 'onChange',
        mode: 'onChange',
    });
    const {
        control,
        reset,
        handleSubmit,
        trigger,
        register,
        formState: { errors, isValid },
    } = rhf;

    const values = useWatch({ control, name: 'address' });

    const isAddressValidated = values ? Object.keys(values).some((key) => Boolean(values[key as keyof CreateAddressDto])) : false;

    useEffect(() => {
        trigger('address');
    }, [isAddressValidated]);

    // cleanup on unmount
    useEffect(() => {
        return () => queryClient.removeQueries({ queryKey: ['physician', params.pharmacyId] });
    }, []);

    // fetch data
    const { isFetched } = useQuery<PharmacyDto | void>({
        queryKey: ['physician', params.pharmacyId],
        queryFn: async () => {
            try {
                const { data, status } = await api.pharmacy.get(params.pharmacyId as string);

                if (status !== 200 || !data) {
                    navigate('/settings/pharmacies');
                }

                reset(data as unknown as CreatePharmacyDto);

                return data;
            } catch {
                navigate('/settings/pharmacies');
            }
        },
        enabled: Boolean(params.pharmacyId),
    });

    // create data
    const { mutate: mutateCreate, isPending } = useMutation<any, unknown, CreatePharmacyDto>({
        mutationFn: async (data) => {
            const response = await api.pharmacy.create(data);
            return response.data;
        },
        onSuccess: () => {
            navigate('/settings/pharmacies');
        },
    });

    // update data
    const { mutate: mutateUpdate } = useMutation<unknown, unknown, UpdatePharmacyDto>({
        mutationFn: async (data) => {
            if (!params.pharmacyId) {
                return;
            }

            const response = await api.pharmacy.edit(params?.pharmacyId, data);

            return response;
        },
        onSuccess: () => {
            navigate('/settings/pharmacies');
        },
    });

    if (params.pharmacyId && !isFetched) {
        return (
            <Spinner
                borderWidth="4px"
                animationDuration="0.65s"
                css={{ '--spinner-track-color': 'purple.100' }}
                color="purple.500"
                size="lg"
            />
        );
    }

    return (
        <Page heading="Pharmacy Details">
            <FormProvider {...rhf}>
                <form
                    noValidate
                    onSubmit={handleSubmit((values) => {
                        const { address, ...rest } = values;

                        if (params.pharmacyId) {
                            mutateUpdate({ ...rest, address: address?.address ? address : undefined });
                        } else {
                            mutateCreate({ ...rest, address: address?.address ? address : undefined });
                        }
                    })}>
                    <Flex gap="8" flexDirection="column">
                        <Card>
                            <FieldGroup>
                                <Field label="Name" required invalid={!!errors.name} errorText={errors.name?.message}>
                                    <Input {...register('name')} />
                                </Field>

                                <Field
                                    label="Address"
                                    required={isAddressValidated}
                                    invalid={!!errors.address?.address}
                                    errorText={errors.address?.address?.message}>
                                    <Input {...register('address.address')} />
                                </Field>

                                <FieldRow>
                                    <Field
                                        label="City"
                                        required={isAddressValidated}
                                        invalid={!!errors.address?.city}
                                        errorText={errors.address?.city?.message}>
                                        <Input {...register('address.city')} />
                                    </Field>

                                    <Field
                                        label="State"
                                        required={isAddressValidated}
                                        invalid={!!errors.address?.state}
                                        errorText={errors.address?.state?.message}>
                                        <NativeSelectRoot>
                                            <NativeSelectField {...rhf.register('address.state')}>
                                                <States />
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>

                                    <Field
                                        label="Zipcode"
                                        required={isAddressValidated}
                                        invalid={!!errors.address?.zipcode}
                                        errorText={errors.address?.zipcode?.message}>
                                        <Input {...register('address.zipcode')} />
                                    </Field>
                                </FieldRow>
                                <Divider colorPalette="gray" />
                                <FieldRow>
                                    <Field label="Phone" required invalid={!!errors.phone} errorText={errors.phone?.message}>
                                        <Controller
                                            name="phone"
                                            control={control}
                                            render={({ field }) => (
                                                <PhoneInput iconSize={16} {...field} onChange={(e) => field.onChange(e.target.value)} />
                                            )}
                                        />
                                    </Field>

                                    <Field label="Fax" invalid={!!errors.fax} errorText={errors.fax?.message}>
                                        <Controller
                                            name="fax"
                                            control={control}
                                            render={({ field }) => (
                                                <FaxInput
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </Card>

                        <Flex alignItems="flex-start" gap="4">
                            <Button type="submit" disabled={!isValid} loading={isPending}>
                                Save
                            </Button>
                            <Link to="/settings/pharmacies">
                                <Button type="button" variant="outline">
                                    Cancel
                                </Button>
                            </Link>
                        </Flex>
                    </Flex>
                </form>
            </FormProvider>
        </Page>
    );
}
